import { FrameFlags, LIMITS, gapBetween, hasFlag } from '@auxcast/protocol';

export type SequenceAction =
  /** Decode and play this frame. */
  | 'play'
  /** Duplicate or reordered frame — throw it away. */
  | 'drop'
  /** Too much is missing: flush the jitter buffer and restart from this frame. */
  | 'reset';

export interface SequenceDecision {
  action: SequenceAction;
  /** Frames to conceal before this one. Never above `LIMITS.MAX_PLC_FRAMES`. */
  conceal: number;
  /** Frames the sender emitted that never arrived, including concealed ones. */
  lost: number;
}

/**
 * Turns `seq` and the DISCONTINUITY flag into what the decoder should do next —
 * docs/PROTOCOL.md §3.2.
 *
 * TCP guarantees ordering, so a gap means the relay dropped frames for
 * backpressure. Up to `MAX_PLC_FRAMES` the decoder conceals; past that the audio
 * on the far side of the gap is unrelated to what came before and bridging it
 * would only smear the artefact, so the buffer is reset instead.
 */
export class SequenceTracker {
  private previous: number | null = null;
  private totalLost = 0;
  private totalConcealed = 0;
  private totalResets = 0;

  get lostFrames(): number {
    return this.totalLost;
  }

  get concealedFrames(): number {
    return this.totalConcealed;
  }

  get resets(): number {
    return this.totalResets;
  }

  reset(): void {
    this.previous = null;
  }

  observe(seq: number, flags: number): SequenceDecision {
    const discontinuity = hasFlag(flags, FrameFlags.DISCONTINUITY);

    if (this.previous === null || discontinuity) {
      this.previous = seq;
      if (discontinuity) this.totalResets += 1;
      return { action: 'reset', conceal: 0, lost: 0 };
    }

    const delta = (seq - this.previous) >>> 0;
    if (delta === 0 || delta > 0x7fffffff) {
      return { action: 'drop', conceal: 0, lost: 0 };
    }

    const missing = gapBetween(this.previous, seq);
    this.previous = seq;

    if (missing === 0) return { action: 'play', conceal: 0, lost: 0 };

    this.totalLost += missing;
    if (missing > LIMITS.MAX_PLC_FRAMES) {
      this.totalResets += 1;
      return { action: 'reset', conceal: 0, lost: missing };
    }

    this.totalConcealed += missing;
    return { action: 'play', conceal: missing, lost: missing };
  }
}
